/**
 * „Wie würden Sie Bauausführung und Ausstattung bewerten?" als Bildauswahl.
 *
 * Vorher ein `<select>` mit drei Wörtern (Gehoben / Normal / Minder). Die
 * Wörter allein sagen niemandem, wo die Grenze liegt — genau deshalb steht
 * unter der Frage im Formular eine Begründungspflicht, sobald jemand von
 * „normal" abweicht. Die Karten zeigen deshalb, woran die Stufe festgemacht
 * wird: Fensterformat samt Verglasung und Bodenbelag (`ausstattungBilder.tsx`).
 *
 * Gespeichert wird unverändert der String aus `AUSSTATTUNGS_STUFEN`
 * („gehoben" / „normal" / „minder") im Antwort-Schlüssel
 * `bauausfuehrungBewertung`; Bestandsdaten hängen daran.
 *
 * Tastaturmuster wie in `BauartklassePicker`/`StellungPicker`: Radiogroup mit
 * Roving-Tabindex, Pfeile/Home/End.
 *
 * **„Auswahl aufheben" ersetzt das „— wählen —" des `<select>`.** Die Frage ist
 * nicht verpflichtend, und das abgelöste Feld hatte über die Leeroption von
 * `QuestionField` immer einen Weg zurück in den unbeantworteten Zustand. Ein
 * Radio lässt sich nicht abwählen — ohne diesen Knopf bliebe ein versehentlich
 * gesetztes „gehoben" für immer stehen, zählte im Fortschritt als beantwortet
 * und hielte den Begründungskasten offen. Der Knopf steht bewusst AUSSERHALB
 * der Radiogroup (ein Nicht-Radio darin wäre fehlerhaftes ARIA) und erscheint
 * erst, wenn es etwas aufzuheben gibt.
 */
import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { AUSSTATTUNGS_STUFEN } from "./enums";
import { ILLU } from "./illustrationTokens";
import { AusstattungsStufeBild, stufenMerkmale } from "./ausstattungBilder";

/**
 * Untergrenze einer Karte. Sie ergibt sich aus der Zeichnung (180 px breit) plus
 * je 12 px Polsterung, aufgerundet auf einen Wert, der in der auf 920 px
 * gedeckelten Inhaltsspalte noch drei Spalten zulässt: 3 x 240 + 2 x 12 = 744.
 */
const KARTE_MIN_PX = 240;

const useStyles = makeStyles({
  leseHinweis: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.84rem",
    lineHeight: 1.4,
    color: BRAND.muted,
    marginTop: "0",
    marginBottom: "10px",
  },
  /**
   * Der Umbruch kommt aus der GEMESSENEN Rasterbreite, nicht aus dem Viewport —
   * dieselbe Begründung wie in `BauartklassePicker`: das Formular läuft auch in
   * einem schwebenden Fenster, das bis 420 px schmal gezogen werden kann,
   * während der Viewport breit bleibt. Eine `@media (min-width: …)`-Stufe
   * lieferte dort drei Spalten in einem ~350 px schmalen Inhaltsbereich.
   *
   * `min(240px, 100%)` statt `240px`: unterschreitet der Container die 240 px,
   * liefe ein blankes `minmax(240px, 1fr)` über den Rand hinaus.
   *
   * `alignItems: start`, damit eine Karte mit längerem Merkmalstext die
   * Nachbarkarten nicht mitwachsen lässt.
   */
  grid: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${KARTE_MIN_PX}px, 100%), 1fr))`,
    gap: "12px",
    alignItems: "start",
  },
  karte: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "hidden",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, transform, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(45,20,43,0.08)",
    },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  // Aktiv nur über Rahmen + Ring, bewusst ohne Flächentönung: die würde den
  // getönten Bildkopf darüber schlucken. Gleich gehalten wie im
  // BauartklassePicker, damit beide Schritte denselben Aktiv-Zustand zeigen.
  karteAktiv: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  bild: {
    backgroundColor: ILLU.headerBg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    display: "flex",
    justifyContent: "center",
  },
  // Kein `padding` auf dem SVG selbst — in apps/web gibt es keinen globalen
  // border-box-Reset, `width: 100%` plus Polsterung liefe hier über.
  svg: {
    width: "100%",
    maxWidth: "180px",
    height: "auto",
    aspectRatio: "120 / 100",
  },
  koerper: {
    paddingTop: "10px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  titel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
  },
  hinweis: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: BRAND.muted,
  },
  merkmale: {
    marginTop: "4px",
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  merkmal: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    lineHeight: 1.3,
    color: BRAND.ink,
  },
  // Eigene Zeile statt eines vierten Rasterfelds: der Knopf ist keine vierte
  // Stufe und dürfte optisch nie wie eine aussehen.
  aufhebenZeile: {
    marginTop: "8px",
    display: "flex",
  },
  aufheben: {
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.borderStyle("none"),
    ...shorthands.padding("0"),
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    lineHeight: 1.4,
    color: BRAND.muted,
    textDecorationLine: "underline",
    ":hover": {
      color: BRAND.purple,
    },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
});

type Props = {
  value: string;
  onChange: (naechste: string) => void;  /** Eindeutige Beschriftung, wenn mehrere Auswahlen auf einer Seite stehen (Anbauten). */
  ariaLabel?: string;
};

const AusstattungsstufePicker = ({ value, onChange, ariaLabel = "Bauausführung und Ausstattung" }: Props) => {
  const styles = useStyles();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  // Roving Tabindex: die gewählte Karte ist tabbar. Bei leerem oder unbekanntem
  // Wert (z. B. aus dem Legacy-Formular) fällt der Fokus auf die erste Karte —
  // sonst wäre die Gruppe per Tastatur gar nicht erreichbar.
  const gewaehlt = AUSSTATTUNGS_STUFEN.findIndex((s) => s.value === value);
  const tabIndexOf = (i: number) => (i === (gewaehlt >= 0 ? gewaehlt : 0) ? 0 : -1);

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const letzter = AUSSTATTUNGS_STUFEN.length - 1;
    let ziel = -1;
    let pfeil = false;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      ziel = i === letzter ? 0 : i + 1;
      pfeil = true;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      ziel = i === 0 ? letzter : i - 1;
      pfeil = true;
    } else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = letzter;
    if (ziel < 0) return;
    e.preventDefault();
    // Ohne Vorauswahl steht der Fokus auf Karte 0, gewählt ist aber nichts —
    // ein Weiterwandern würde „gehoben" stillschweigend überspringen.
    const naechste = pfeil && gewaehlt < 0 ? i : ziel;
    refs.current[naechste]?.focus();
    onChange(AUSSTATTUNGS_STUFEN[naechste].value);
  };

  const handleAufheben = () => {
    // Erst den Fokus umsetzen, dann leeren: der Knopf verschwindet mit dem
    // nächsten Render (er hängt an `value !== ""`), und ein Fokus auf einem
    // ausgehängten Element fällt auf <body> zurück — die Tastaturbedienung
    // stünde danach am Seitenanfang. Ziel ist die eben abgewählte Karte; bei
    // einem unbekannten Bestandswert gibt es keine, dann die erste.
    refs.current[gewaehlt >= 0 ? gewaehlt : 0]?.focus();
    onChange("");
  };

  return (
    <div>
      {/* Der Hinweis steht ÜBER dem Raster, nicht darunter: ohne ihn läse sich
          das Bild als Vorschrift („nur mit Parkett ist es gehoben"), und diese
          Auskunft braucht man beim Ansehen der Karten, nicht danach. */}
      <p className={styles.leseHinweis}>
        Die Bilder zeigen typische Merkmale der Stufe — Fensterformat, Verglasung und Bodenbelag. Maßgeblich ist Ihr
        Gesamteindruck, nicht das einzelne Merkmal.
      </p>

      <div className={styles.grid} role="radiogroup" aria-label={ariaLabel}>
        {AUSSTATTUNGS_STUFEN.map((stufe, i) => {
          const aktiv = value === stufe.value;
          return (
            <button
              type="button"
              key={stufe.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              role="radio"
              aria-checked={aktiv}
              tabIndex={tabIndexOf(i)}
              className={mergeClasses(styles.karte, aktiv && styles.karteAktiv)}
              onClick={() => onChange(stufe.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              <span className={styles.bild}>
                {/* `dekorativ`: Titel, Hinweis und die beiden Merkmalszeilen
                    stehen direkt darunter als Text — die Zeichnung wäre im
                    Vorlesenamen der Karte eine Dublette. */}
                <AusstattungsStufeBild stufe={stufe.value} className={styles.svg} dekorativ />
              </span>
              <span className={styles.koerper}>
                <span className={styles.titel}>{stufe.label}</span>
                <span className={styles.hinweis}>{stufe.hinweis}</span>
                <span className={styles.merkmale}>
                  {stufenMerkmale(stufe.value).map((zeile) => (
                    <span key={zeile} className={styles.merkmal}>
                      {zeile}
                    </span>
                  ))}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* `value !== ""` statt `gewaehlt >= 0`: auch ein unbekannter Bestandswert
          („sehr gehoben") ist eine Antwort, die sich löschen lassen muss —
          über die Karten wäre sie sonst gar nicht erreichbar.
          Die bereits getippte Begründung bleibt bewusst stehen: das
          abgelöste `<select>` hat sie beim Zurücksetzen auf „— wählen —" auch
          nur ausgeblendet, nicht verworfen. */}
      {value !== "" && (
        <div className={styles.aufhebenZeile}>
          <button type="button" className={styles.aufheben} onClick={handleAufheben}>
            Auswahl aufheben
          </button>
        </div>
      )}
    </div>
  );
};

export default AusstattungsstufePicker;
